import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { trackPageView, trackToolSelected } from "@/lib/utils/analytics";
import {
  MapPin, FileJson, CheckCircle, Circle, Minimize2,
  Calculator, GitMerge, Scissors, Crosshair, Share2, Layers, Layout,
} from "lucide-react";
import LeftPanel from "@/components/LeftPanel";
import NotFound from "@/pages/NotFound";
import DonutWheel from "@/components/DonutWheel";
import MobileDonut from "@/components/MobileDonut";
import FeedbackModal from "@/components/FeedbackModal";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import FAQ from "@/components/FAQ";
import Features from "@/components/Features";
import { MapPreviewProvider, useMapPreview } from "@/contexts/MapPreviewContext";
import { ConvertPresetContext } from "@/contexts/ConvertPresetContext";
import { t } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";
import { getToolSeo, getConvertSeo, CONVERT_HUB, DEFAULT_TITLE, DEFAULT_DESC } from "@/seo/seo.config";

const MapView = lazy(() => import("@/components/MapView"));

const TOOLS = [
  { id: "reproject",  icon: MapPin,      label: "Reproject"    },
  { id: "converter",  icon: FileJson,    label: "Convert"      },
  { id: "validate",   icon: CheckCircle, label: "Validate"     },
  { id: "buffer",     icon: Circle,      label: "Buffer"       },
  { id: "simplify",   icon: Minimize2,   label: "Simplify"     },
  { id: "calculate",  icon: Calculator,  label: "Calculate"    },
  { id: "merge",      icon: GitMerge,    label: "Merge"        },
  { id: "clip",       icon: Scissors,    label: "Clip"         },
  { id: "coordinate", icon: Crosshair,   label: "Coordinates"  },
  { id: "layout",     icon: Layout,      label: "Layout", badge: "NEW" },
  { id: "share",      icon: Share2,      label: "Share",  badge: "NEW" },
];

const TOOL_NAMES: Record<string, string> = {
  reproject:  "Reproject",
  converter:  "Format Converter",
  validate:   "Validate & Fix",
  buffer:     "Buffer Analysis",
  simplify:   "Simplify Geometry",
  calculate:  "Measurements",
  merge:      "Merge Shapefiles",
  clip:       "Clip by Extent",
  coordinate: "Coordinates",
  layout:     "Layout Peta",
  share:      "Share & Bagikan",
};

// Routed entries (e.g. Visualize) navigate to their own page, not /tools/<id>.
const VALID_TOOLS = new Set(TOOLS.filter(t => !("route" in t)).map(t => t.id));

function setMeta(selector: string, attr: string, value: string) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

// Inner component so it can use the MapPreview context
const AppContent = () => {
  const { toolId, pair } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // URL is the source of truth. Three shapes:
  //   /tools/<id>        -> that tool
  //   /convert           -> converter tool, hub SEO
  //   /convert/<pair>    -> converter tool, locked From->To + per-pair SEO
  const onConvert = location.pathname === "/convert" || location.pathname.startsWith("/convert/");
  const convertSeo = pair ? getConvertSeo(pair) : null;
  const convertSlug = onConvert ? (pair && convertSeo ? pair : "hub") : null;
  const convertPreset = convertSeo ? { input: convertSeo.input, output: convertSeo.output } : null;
  const activeTool = onConvert ? "converter" : (toolId && VALID_TOOLS.has(toolId) ? toolId : null);

  // A tool id or conversion pair we don't have is a 404, not the homepage.
  // Serving the landing page at /tools/<anything> makes every made-up URL a
  // soft 404: HTTP 200 with content that duplicates the homepage, which search
  // engines are free to index.
  const unknownRoute =
    (!!toolId && !VALID_TOOLS.has(toolId)) ||
    (!!pair && !convertSeo);

  const [toolKey,    setToolKey]    = useState(0);
  const [sheet,      setSheet]      = useState<"faq" | "about" | null>(null);
  const [feedback,   setFeedback]   = useState(false);
  const { geoJSON, mapOpen, setMapOpen, setGeoJSON } = useMapPreview();
  useLocale(); // re-render sheet headers when the language toggles

  useEffect(() => { trackPageView(); }, []);

  // Keep <title>, description and canonical in sync with the active tool so each
  // tool URL is self-describing for users and crawlers that render JS.
  useEffect(() => {
    let title = DEFAULT_TITLE, desc = DEFAULT_DESC, url = "https://www.shpaid.com/";
    if (onConvert) {
      if (convertSeo) {
        title = convertSeo.title; desc = convertSeo.description; url = `https://www.shpaid.com${convertSeo.slug}`;
      } else {
        title = CONVERT_HUB.title; desc = CONVERT_HUB.description; url = "https://www.shpaid.com/convert";
      }
    } else {
      const seo = getToolSeo(activeTool);
      if (seo) { title = seo.title; desc = seo.description; url = `https://www.shpaid.com/tools/${activeTool}`; }
    }
    document.title = title;
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Clear any stale map preview whenever the active tool changes. The tool then
  // repopulates it after its own operation completes.
  useEffect(() => {
    setGeoJSON(null);
    setMapOpen(false);
  }, [activeTool, setGeoJSON, setMapOpen]);

  const handleToolSelect = (id: string) => {
    // Routed entries (Visualize) open their own full-screen page.
    const routed = TOOLS.find(t => t.id === id) as { route?: string } | undefined;
    if (routed?.route) { trackToolSelected(id); navigate(routed.route); return; }
    setToolKey(k => k + 1);
    const next = activeTool === id ? null : id;
    if (next) trackToolSelected(next);
    navigate(next ? `/tools/${next}` : "/");
  };

  const handleDeselect = () => {
    setToolKey(k => k + 1);
    navigate("/");
  };

  if (unknownRoute) return <NotFound />;

  return (
    <ConvertPresetContext.Provider value={convertPreset}>
      {/* ── Main layout ── */}
      <main id="main-content" className="bg-background flex flex-col md:flex-row h-screen h-[100dvh] overflow-hidden">
        <LeftPanel
          activeTool={activeTool}
          convertSlug={convertSlug}
          toolKey={toolKey}
          onOpenFAQ={()      => setSheet("faq")}
          onOpenAbout={()    => setSheet("about")}
          onOpenFeedback={()  => setFeedback(true)}
        />

        {/* Right panel: full DonutWheel on desktop, half-donut at bottom on mobile/tablet.
            No overflow clip here so a hovered slice can grow past the panel edge over
            the left panel; the off-screen-right 40% is clipped by <main> instead.
            z-10 keeps the donut painted above the left panel during that pop-out. */}
        <div className="w-full md:w-[372px] flex-shrink-0 md:h-screen relative md:z-10">
          {/* Desktop: full DonutWheel anchored to right edge */}
          <div className="hidden md:flex h-full">
            <DonutWheel
              tools={TOOLS}
              activeTool={activeTool}
              onToolSelect={handleToolSelect}
              onDeselect={handleDeselect}
            />
          </div>

          {/* Mobile + tablet: half-donut anchored to bottom */}
          <div className="md:hidden mt-2">
            <MobileDonut
              tools={TOOLS}
              activeTool={activeTool}
              onToolSelect={handleToolSelect}
              onDeselect={handleDeselect}
            />
          </div>
        </div>
      </main>

      {/* ── Floating map preview ── */}
      {mapOpen && geoJSON && (
        <Suspense fallback={null}>
          <MapView
            geoJSON={geoJSON}
            toolLabel={activeTool ? TOOL_NAMES[activeTool] : undefined}
            onClose={() => setMapOpen(false)}
          />
        </Suspense>
      )}

      {/* ── FAQ sheet ── */}
      <Sheet open={sheet === "faq"} onOpenChange={o => !o && setSheet(null)}>
        <SheetContent side="left" className="w-full max-w-md overflow-y-auto">
          <SheetTitle className="mb-1">{t("sheet.faqTitle")}</SheetTitle>
          <SheetDescription className="mb-4">
            {t("sheet.faqDesc")}
          </SheetDescription>
          <div className="[&>section]:py-0 [&>section]:px-0">
            <FAQ />
          </div>
        </SheetContent>
      </Sheet>

      {/* ── About sheet ── */}
      <Sheet open={sheet === "about"} onOpenChange={o => !o && setSheet(null)}>
        <SheetContent side="left" className="w-full max-w-md overflow-y-auto">
          <SheetTitle className="mb-1">{t("sheet.aboutTitle")}</SheetTitle>
          <SheetDescription className="mb-4">
            {t("sheet.aboutDesc")}
          </SheetDescription>
          <div className="[&>section]:py-4 [&>section]:px-0">
            <Features />
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Feedback modal ── */}
      <FeedbackModal open={feedback} onOpenChange={setFeedback} />
    </ConvertPresetContext.Provider>
  );
};

const Index = () => (
  <MapPreviewProvider>
    <AppContent />
  </MapPreviewProvider>
);

export default Index;
